import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, CardBody, CardTitle, Dialog, Field } from '../../ui';
import {
  countTemplateWeeks,
  deleteTemplate,
  listTemplates,
  renameTemplate,
} from '../../shared/queries';
import type { PlanTemplateRow } from '../../shared/queries';
import { formatMoment } from '../../shared/formatDate';
import { isDuplicateName, matchesName, SEARCH_APPEARS_AT } from '../../shared/templateNames';
import { supabase } from '../../shared/supabase/client';
import { DELETE_LEAVES_PLANS, weekCountLabel } from './templateCopy';

/**
 * Her templates: the first screen in the admin area that belongs to the coach
 * rather than to one of her clients.
 *
 * The owner's brief for it was about speed of recognition, not about features —
 * "the coach can have many different templates that can be used to initiate a
 * plan to a customer" — so the row shows the three things that tell one apart
 * from another: what she called it, how long it is, and whether she has reached
 * for it lately. Most recently used first, because the one she used last week is
 * the one she is most likely to want again, and never-used ones after, because
 * they are the ones she is still deciding about.
 *
 * There is no "new template" here and that is deliberate: a template is made by
 * saving a plan she has already built (`plan-templates`, "A template is made by
 * saving a plan"). The empty state says so, because an empty list that does not
 * say where templates come from is a dead end.
 */
export function TemplatesPage() {
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => listTemplates(supabase),
  });

  const weekCounts = useQuery({
    queryKey: ['template-week-counts'],
    queryFn: () => countTemplateWeeks(supabase),
  });

  const [query, setQuery] = useState('');
  const [renaming, setRenaming] = useState<PlanTemplateRow | null>(null);
  const [deleting, setDeleting] = useState<PlanTemplateRow | null>(null);

  const rows = templates.data ?? [];
  const searchable = rows.length >= SEARCH_APPEARS_AT;
  const shown = searchable ? rows.filter((row) => matchesName(row.name, query)) : rows;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-ink text-2xl font-semibold">Templates</h1>
        <p className="text-ink-soft mt-1 text-sm">
          Programming you can start a client&rsquo;s plan from. Applying one copies its weeks in as
          drafts, and the plan is yours to change from there.
        </p>
      </div>

      {templates.isPending ? <p className="text-ink-muted text-sm">Loading…</p> : null}

      {templates.isError ? (
        <p role="alert" className="text-danger text-sm">
          Your templates could not be loaded.
        </p>
      ) : null}

      {/*
        Offered only once the list has outgrown a screenful. Below that the
        search field is one more thing to read on a page whose whole job is to
        be read at a glance.
      */}
      {searchable ? (
        <Field
          label="Find a template"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className="max-w-sm"
        />
      ) : null}

      {!templates.isPending && rows.length === 0 ? <EmptyState /> : null}

      {searchable && shown.length === 0 ? (
        <Card>
          <CardBody>No template of yours has that in its name.</CardBody>
        </Card>
      ) : null}

      {shown.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {shown.map((template) => (
            <li key={template.id}>
              <TemplateRow
                template={template}
                weeks={weekCounts.data?.get(template.id) ?? 0}
                weeksKnown={weekCounts.isSuccess}
                onRename={() => {
                  setRenaming(template);
                }}
                onDelete={() => {
                  setDeleting(template);
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {renaming === null ? null : (
        <RenameDialog
          template={renaming}
          existing={rows}
          onClose={() => {
            setRenaming(null);
          }}
        />
      )}

      {deleting === null ? null : (
        <DeleteDialog
          template={deleting}
          onClose={() => {
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardTitle>No templates yet</CardTitle>
      <CardBody>
        A template is a plan you keep: its weeks, sessions and exercises, without anyone&rsquo;s
        loads or your notes to them. You make one by opening a client, building a plan you are happy
        with, and choosing “Save as template” on it. After that you can start any client&rsquo;s
        plan from it in one go.
      </CardBody>
      {/*
        The route forward, not a dead end: the only way to make a template
        starts on a client's plan, so the empty state points at the roster
        rather than leaving her to work out where to go.
      */}
      <div className="mt-4">
        <Link
          to="/admin"
          className="text-primary text-sm font-medium underline-offset-2 hover:underline"
        >
          Open a client and build a plan
        </Link>
      </div>
    </Card>
  );
}

function TemplateRow({
  template,
  weeks,
  weeksKnown,
  onRename,
  onDelete,
}: {
  template: PlanTemplateRow;
  weeks: number;
  weeksKnown: boolean;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-ink font-medium">{template.name}</p>
        <p className="text-ink-muted text-xs">
          {weeksKnown ? weekCountLabel(weeks) : '…'}
          {' · '}
          {template.last_used_at === null
            ? 'Never used'
            : `Last used ${formatMoment(template.last_used_at)}`}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onRename}>
          Rename
        </Button>
        {/*
          The pair was two teal words. "Rename" is an ordinary action and
          "Delete" destroys a template and its weeks; they must not read alike
          at a glance (design-system, "The template row").
        */}
        <Button variant="danger-ghost" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </Card>
  );
}

/** The database's own limit, repeated here so the form can say so first. */
const NAME_MAX = 80;

function RenameDialog({
  template,
  existing,
  onClose,
}: {
  template: PlanTemplateRow;
  existing: readonly PlanTemplateRow[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template.name);

  const trimmed = name.trim();
  const error =
    trimmed === ''
      ? 'Give the template a name so you can find it again.'
      : trimmed.length > NAME_MAX
        ? `Keep the name to ${String(NAME_MAX)} characters or fewer.`
        : undefined;

  const duplicate = isDuplicateName(
    existing.filter((row) => row.id !== template.id).map((row) => row.name),
    trimmed,
  );

  const rename = useMutation({
    mutationFn: () => renameTemplate(supabase, template.id, trimmed),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} title="Rename template">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (error === undefined) rename.mutate();
        }}
      >
        <Field
          label="Template name"
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => {
            setName(event.target.value);
          }}
          error={error}
        />

        <p className="text-ink-soft text-sm leading-relaxed">
          Only the template is renamed. Every plan you have already started from it is untouched.
        </p>

        {duplicate ? (
          <p role="status" className="text-attention text-sm">
            You already have a template with this name. You can still save it.
          </p>
        ) : null}

        {rename.isError ? (
          <p role="alert" className="text-danger text-sm">
            {rename.error.message}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={error !== undefined || rename.isPending}>
            {rename.isPending ? 'Saving…' : 'Save name'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeleteDialog({ template, onClose }: { template: PlanTemplateRow; onClose: () => void }) {
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => deleteTemplate(supabase, template.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      await queryClient.invalidateQueries({ queryKey: ['template-week-counts'] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} title="Delete this template?">
      <p className="text-ink-soft mt-3 text-sm leading-relaxed">
        “{template.name}” and its weeks will be removed. {DELETE_LEAVES_PLANS}
      </p>

      {remove.isError ? (
        <p role="alert" className="text-danger mt-4 text-sm">
          {remove.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Keep it
        </Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          {remove.isPending ? 'Deleting…' : 'Delete template'}
        </Button>
      </div>
    </Dialog>
  );
}
